import VectorSource from 'ol/source/Vector.js';
import VectorLayer from "ol/layer/Vector.js";
import Draw from 'ol/interaction/Draw.js';
import Overlay from 'ol/Overlay.js';
import Polygon from 'ol/geom/Polygon.js';
import { unByKey } from 'ol/Observable.js';
import { mapActions, mapState } from 'vuex';

// Persistent state across component mounts
let persistentDrawSource = null;
let persistentDrawLayer = null;

// Ellipse Geometry Function: Rotated version (64 points)
const ellipseGeometryFunction = (coordinates, geometry) => {
    if (!geometry) {
        geometry = new Polygon([]);
    }
    const center = coordinates[0];
    const corner = coordinates[1];
    const dx = corner[0] - center[0];
    const dy = corner[1] - center[1];
    
    const rx = Math.hypot(dx, dy);
    const ry = rx * 0.5; // Fixed aspect ratio for visual consistency
    const rotation = Math.atan2(dy, dx);
    
    const points = [];
    const numPoints = 64;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);

    for (let i = 0; i < numPoints; i++) {
        const angle = (i / numPoints) * 2 * Math.PI;
        const x = rx * Math.cos(angle);
        const y = ry * Math.sin(angle);
        
        // Apply rotation and translate to center
        points.push([
            center[0] + x * cos - y * sin,
            center[1] + x * sin + y * cos
        ]);
    }
    points.push(points[0]);
    geometry.setCoordinates([points]);
    return geometry;
};

// Arrow Geometry Function: 7-point polygon
const arrowGeometryFunction = (coordinates, geometry) => {
    if (!geometry) {
        geometry = new Polygon([]);
    }
    const start = coordinates[0];
    const end = coordinates[1];
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const length = Math.hypot(dx, dy);
    
    if (length === 0) return geometry;
    
    const angle = Math.atan2(dy, dx);
    const headLen = length * 0.25; 
    const headWid = length * 0.15;
    const shaftWid = length * 0.05;

    // Define arrow shape in local coordinates
    const points = [
        [0, -shaftWid / 2],
        [length - headLen, -shaftWid / 2],
        [length - headLen, -headWid / 2],
        [length, 0],
        [length - headLen, headWid / 2],
        [length - headLen, shaftWid / 2],
        [0, shaftWid / 2],
        [0, -shaftWid / 2]
    ];

    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const transformedPoints = points.map(p => [
        start[0] + p[0] * cos - p[1] * sin,
        start[1] + p[0] * sin + p[1] * cos
    ]);

    geometry.setCoordinates([transformedPoints]);
    return geometry;
};


export const draw = {
    data () {
        return {
            drawSource: null,
            drawLayer: null,
            drawInteraction: null,
            helpTooltipElement: null,
            helpTooltipOverlay: null,
            pointerMoveKey: null,
            sketch: null
        }
    },
    computed: {
        ...mapState('draw', ['undoStack', 'redoStack']),
        canUndo() { return this.undoStack.length > 0; },
        canRedo() { return this.redoStack.length > 0; }
    },
    methods: {
        ...mapActions('draw', ['pushFeature', 'undo', 'redo', 'clearHistory']),
        initDrawLayer: function () {
            // Persistence: Restore from module scope
            if (persistentDrawLayer) {
                this.drawLayer = persistentDrawLayer;
                this.drawSource = persistentDrawSource;
                this.drawLayer.setVisible(true);
                return;
            } 
            this.drawSource = new VectorSource();
            this.drawLayer = new VectorLayer({
                source: this.drawSource,
                style: this.createDrawStyle(),
            });
            persistentDrawSource = this.drawSource;
            persistentDrawLayer = this.drawLayer;
            this.map.addLayer(this.drawLayer)
        },
        initDrawInteraction: function (drawingType) {
            // Clean previous interaction and tooltip
            if (this.drawInteraction) {
                this.map.removeInteraction(this.drawInteraction);
            }
            this.removeHelpTooltip();

            let options = {
                source: this.drawSource,
                style: this.createDrawStyle(),
            };

            // Specialized interaction patterns
            if (drawingType === 'Freehand') {
                options.type = 'LineString'; 
                options.freehand = true;
            } else if (drawingType === 'Arrow') {
                options.type = 'LineString';
                options.maxPoints = 2;
                options.geometryFunction = arrowGeometryFunction;
            } else if (drawingType === 'Ellipse') {
                options.type = 'LineString';
                options.maxPoints = 2;
                options.geometryFunction = ellipseGeometryFunction;
            } else if (drawingType === 'Text') {
                options.type = 'Point';
            } else {
                options.type = drawingType;
            }

            this.drawInteraction = new Draw(options);
            this.map.addInteraction(this.drawInteraction);

            this.createHelpTooltip();
            this.pointerMoveKey = this.map.on('pointermove', this.handlePointerMove);

            this.drawInteraction.on('drawstart', (evt) => {
                this.sketch = evt.feature;
            });

            this.drawInteraction.on('drawend', (evt) => {
                this.sketch = null;
                this.onDrawEnd(evt);
            });
        },
        createHelpTooltip: function () {
            if (this.helpTooltipElement) {
                this.helpTooltipElement.parentNode.removeChild(this.helpTooltipElement);
            }
            this.helpTooltipElement = document.createElement('div');
            this.helpTooltipElement.className = 'tooltip hidden';
            this.helpTooltipOverlay = new Overlay({
                element: this.helpTooltipElement,
                offset: [15, 0],
                positioning: 'center-left',
            });
            this.map.addOverlay(this.helpTooltipOverlay);
        },
        removeHelpTooltip: function () {
            if (this.helpTooltipOverlay) {
                this.map.removeOverlay(this.helpTooltipOverlay);
                this.helpTooltipOverlay = null;
            }
            if (this.helpTooltipElement && this.helpTooltipElement.parentNode) {
                this.helpTooltipElement.parentNode.removeChild(this.helpTooltipElement);
                this.helpTooltipElement = null;
            }
            if (this.pointerMoveKey) {
                unByKey(this.pointerMoveKey);
                this.pointerMoveKey = null;
            }
            this.sketch = null;
        },
        handlePointerMove: function (evt) {
            if (evt.dragging || !this.helpTooltipElement) {
                return;
            }
            let helpMsg = 'Haga click para empezar a dibujar';
            const type = this.currentDrawingType;

            if (this.sketch) {
                if (type === 'LineString' || type === 'Polygon') {
                    helpMsg = 'Haga click para continuar. Doble click para finalizar.';
                } else if (type === 'Circle') {
                    helpMsg = 'Mueva el cursor para definir el radio y haga click para finalizar.';
                } else if (type === 'Arrow' || type === 'Ellipse') {
                    helpMsg = 'Haga click para finalizar.';
                }
            } else {
                if (type === 'Point') {
                    helpMsg = 'Haga click para situar el punto';
                } else if (type === 'Text') {
                    helpMsg = 'Haga click para situar el texto';
                } else if (type === 'Freehand') {
                    helpMsg = 'Haga click y arrastre para dibujar';
                } else if (type === 'Circle') {
                    helpMsg = 'Haga click para fijar el centro';
                }
            }

            this.helpTooltipElement.innerHTML = helpMsg;
            this.helpTooltipOverlay.setPosition(evt.coordinate);
            this.helpTooltipElement.classList.remove('hidden');
            },
            undoAction: function () {
            this.undo().then(feature => {
                if (feature) {
                    this.drawSource.removeFeature(feature);
                }
            });
            },
            redoAction: function () {
            this.redo().then(feature => {
                if (feature) {
                    this.drawSource.addFeature(feature);
                }
            });
            },
            clearAllDrawings: function () {
            if (window.confirm("¿Borrar todos los dibujos?")) {
                this.drawSource.clear();
                this.clearHistory();
            }
            }
            },
            }